import {
  mergeAttribution,
  normalizeAttribution,
  selectConversionAttribution,
  type Attribution,
} from "@/lib/attribution";
import {
  buildSignupAttributionFromSearch,
  SIGNUP_ATTRIBUTION_STORAGE_KEY,
  SIGNUP_LAST_ATTRIBUTION_STORAGE_KEY,
} from "@/lib/signup-attribution";

const ATTRIBUTION_VISITOR_ID_STORAGE_KEY = "tsb_attribution_visitor_id";
export const BROWSER_ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000;
const MAX_FUTURE_CLOCK_SKEW_MS = 5 * 60 * 1000;

/** Remove optional acquisition state immediately when consent is withdrawn. */
export function clearBrowserAttributionStorage(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(ATTRIBUTION_VISITOR_ID_STORAGE_KEY);
    window.localStorage.removeItem(SIGNUP_ATTRIBUTION_STORAGE_KEY);
    window.localStorage.removeItem(SIGNUP_LAST_ATTRIBUTION_STORAGE_KEY);
    window.localStorage.removeItem("tsb_referral_code");
  } catch {
    // Cookies remain the server authority. Storage is an optional browser
    // mirror and can be unavailable in restricted/sandboxed contexts.
  }
}

function isExternalReferrer(referrer: string): boolean {
  try {
    return new URL(referrer).origin !== window.location.origin;
  } catch {
    return true;
  }
}

export function readCookieValue(name: string): string | null {
  if (typeof document === "undefined") return null;
  for (const part of document.cookie.split(";")) {
    const separator = part.indexOf("=");
    if (separator < 0 || part.slice(0, separator).trim() !== name) continue;
    try {
      return decodeURIComponent(part.slice(separator + 1).trim());
    } catch {
      return null;
    }
  }
  return null;
}

export type BrowserCookieConsent = "accepted" | "declined" | null;

/**
 * Parse the authoritative optional-cookie choice using the same fail-closed
 * semantics as lib/server-cookie-consent.ts.
 */
export function parseCookieConsent(cookieHeader: string): BrowserCookieConsent {
  const values = cookieHeader.split(";").flatMap((part) => {
    const separator = part.indexOf("=");
    if (separator < 0 || part.slice(0, separator).trim() !== "cookie_consent") {
      return [];
    }
    try {
      return [decodeURIComponent(part.slice(separator + 1).trim())];
    } catch {
      return [null];
    }
  });

  if (values.length === 0) return null;
  return values.every((value) => value === "accepted")
    ? "accepted"
    : "declined";
}

/**
 * Browsers can expose duplicate same-name cookies from different paths.
 * Consent is accepted only when every visible value is exactly "accepted".
 */
export function readCookieConsent(): BrowserCookieConsent {
  if (typeof document === "undefined") return null;
  return parseCookieConsent(document.cookie);
}

export function parseGaClientId(
  gaCookie: string | null | undefined,
): string | null {
  if (!gaCookie) return null;
  const match = gaCookie.match(/GA1\.\d+\.(\d+\.\d+)/);
  return match?.[1] ?? null;
}

export function isFreshBrowserAttribution(
  attribution: Attribution | null,
  nowMs = Date.now(),
): attribution is Attribution {
  if (!attribution?.capturedAt) return false;
  const capturedAtMs = Date.parse(attribution.capturedAt);
  return (
    Number.isFinite(capturedAtMs) &&
    capturedAtMs <= nowMs + MAX_FUTURE_CLOCK_SKEW_MS &&
    nowMs - capturedAtMs <= BROWSER_ATTRIBUTION_TTL_MS
  );
}

function readStoredAttributionKey(storageKey: string): Attribution | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    const attribution = raw ? normalizeAttribution(JSON.parse(raw)) : null;
    if (!attribution) return null;
    if (!isFreshBrowserAttribution(attribution)) {
      window.localStorage.removeItem(storageKey);
      return null;
    }
    return attribution;
  } catch {
    return null;
  }
}

export function readStoredAttribution(): Attribution | null {
  return readStoredAttributionKey(SIGNUP_ATTRIBUTION_STORAGE_KEY);
}

/** Whether the visitor separately granted Google's ad-user-data consent. */
export function readAdUserDataConsent(): boolean {
  return readCookieConsent() === "accepted";
}

export function readStoredLastAttribution(): Attribution | null {
  return readStoredAttributionKey(SIGNUP_LAST_ATTRIBUTION_STORAGE_KEY);
}

function readAnalyticsCookieAttribution(): Attribution | null {
  const gaClientId = parseGaClientId(readCookieValue("_ga"));
  const posthogAnonId = readCookieValue("ph_anon_id");

  return normalizeAttribution({
    gaClientId,
    posthogAnonId,
  });
}

export function readCurrentUrlAttribution(): Attribution | null {
  if (typeof window === "undefined") return null;

  const params = new URLSearchParams(window.location.search);
  const hasAcquisitionParam = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "utm_offer",
    "gclid",
    "gbraid",
    "wbraid",
    "msclkid",
    "gad_source",
    "gad_campaignid",
    "ref",
  ].some((key) => Boolean(params.get(key)));
  const referrer = document.referrer || null;
  const hasExternalReferrer = Boolean(referrer && isExternalReferrer(referrer));
  if (!hasAcquisitionParam && !hasExternalReferrer) return null;

  return normalizeAttribution(
    buildSignupAttributionFromSearch(
      params,
      referrer,
      `${window.location.pathname}${window.location.search}`,
      window.location.pathname,
      new Date().toISOString(),
    ),
  );
}

export function readBrowserAttribution(): Attribution | null {
  // This helper is used by lead, onboarding, and setup-service callers as well
  // as analytics UI. Stored campaign data and analytics cookie IDs are
  // optional attribution, so keep the lowest shared read boundary fail-closed.
  if (readCookieConsent() !== "accepted") return null;

  return mergeAttribution(
    readStoredAttribution(),
    readAnalyticsCookieAttribution(),
  );
}

/**
 * Attribution for a commercial conversion. First touch remains available for
 * acquisition reporting, while the most recent paid touch wins for Ads
 * matching and checkout optimisation.
 */
export function readBrowserConversionAttribution(): Attribution | null {
  // Conversion attribution can contain Google click identifiers and analytics
  // cookie IDs. Keep the shared boundary fail-closed so every checkout and lead
  // form caller inherits the optional-cookie decision automatically.
  if (readCookieConsent() !== "accepted") return null;

  return selectConversionAttribution(
    readStoredAttribution(),
    readStoredLastAttribution(),
    readCurrentUrlAttribution(),
    readAnalyticsCookieAttribution(),
  );
}

export function readBrowserAttributionWithCurrentUrl(): Attribution | null {
  return readBrowserConversionAttribution();
}
